import { parseToHsl, rgba } from 'polished';
import { createTheme, PaletteMode } from '@mui/material';
import { OnlinePagesBasicConfigurationDto } from './api/dtos';
import { isValidFont } from './fonts/is-valid';
import { getFontFamily } from './fonts/parser';

export type Theme = OnlinePagesBasicConfigurationDto['theme'];

type DarkThemeType = 'DEFAULT' | 'PERSONALISED_OFFER';

export const initializeConfigStyles = async (
    config: OnlinePagesBasicConfigurationDto,
    cssUrl?: string,
    darkThemeType?: DarkThemeType
) => {
    await setTheme(config.theme, darkThemeType);
    setCssVariables(config);
    if (cssUrl) {
        setCustomCss(cssUrl);
    }
};

const importDarkThemeVariant = async (variant: DarkThemeType) => {
    switch (variant) {
        case 'DEFAULT':
            // @ts-ignore
            await import('../styles/themes/dark.scss').then();
            break;
        case 'PERSONALISED_OFFER':
            // @ts-ignore
            await import('../views/personalised-offer/themes/dark.scss').then();
            break;
    }
};

const setTheme = async (
    theme?: OnlinePagesBasicConfigurationDto['theme'],
    darkThemeType: DarkThemeType = 'DEFAULT'
) => {
    switch (theme) {
        case 'DARK':
            await importDarkThemeVariant(darkThemeType);
            return;
        default:
            return;
    }
};

export const setGlobalTheme = async (
    config: OnlinePagesBasicConfigurationDto
) => {
    const { colors = {}, roundCorners, fontPrimary, theme } = config;

    return createTheme({
        palette: {
            primary: {
                main: colors.primary ?? rgba(0, 0, 0, 0.07),
                contrastText: colors.text,
                light: theme
            },
            secondary: {
                main: colors.secondary ?? rgba(0, 0, 0, 0.5)
            },
            error: {
                main: colors.error ?? '#e52854'
            },
            success: {
                main: colors.success ?? rgba(0, 128, 0)
            },
            mode: (theme?.toLocaleLowerCase() || 'light') as PaletteMode
        },
        shape: {
            borderRadius: roundCorners ? 10 : 0
        },
        typography: {
            fontFamily: fontPrimary?.type
        }
    });
};

const setCssVariables = (config: OnlinePagesBasicConfigurationDto) => {
    const {
        colors = {},
        roundCorners,
        fontPrimary,
        fontSecondary,
        theme
    } = config;

    const primary = colors.primary || null;
    const secondary = colors.secondary || primary;

    document.documentElement.style.setProperty('--primary', primary);
    document.documentElement.style.setProperty(
        '--primarylight',
        primary && rgba(primary, 0.1)
    );
    document.documentElement.style.setProperty(
        '--primarylighthover',
        primary && rgba(primary, 0.2)
    );
    document.documentElement.style.setProperty(
        '--primarydarkhover',
        primary && rgba(primary, 0.8)
    );
    document.documentElement.style.setProperty(
        '--primary-contrast',
        primary && calculateContrastColor(primary)
    );

    document.documentElement.style.setProperty('--secondary', secondary);
    document.documentElement.style.setProperty(
        '--secondary-contrast',
        secondary && calculateContrastColor(secondary)
    );

    document.documentElement.style.setProperty(
        '--theme-contrast',
        theme === 'LIGHT' ? 'black' : 'white'
    );

    // @todo - error and success colors

    document.documentElement.style.setProperty(
        '--borderRadius',
        roundCorners ? '10px' : '0px'
    );

    if (isValidFont(fontPrimary)) {
        document.documentElement.style.setProperty(
            '--main-font',
            getFontFamily(fontPrimary.type)
        );
    }

    if (isValidFont(fontSecondary)) {
        document.documentElement.style.setProperty(
            '--second-font',
            getFontFamily(fontSecondary.type)
        );
    }
};

const calculateContrastColor = (color: string) =>
    parseToHsl(color).lightness >= 0.5 ? 'black' : 'white';

const setCustomCss = (cssUrl: string) => {
    document.head.insertAdjacentHTML(
        'beforeend',
        `<link rel='stylesheet' href='${cssUrl}'>`
    );
};
